/**
 * EmailPreview — F-007 Phase 4 (IMPL-P4-005).
 *
 * Anteprima dell'email di "pratica bloccata" che il cliente riceverà
 * (Brevo + template `blocked-notification-email.ts`). Componente puro:
 *   - nessun fetch
 *   - nessun stato interno
 *   - pure render in JSX della stessa struttura del template HTML lato API
 *
 * Le label IT-IT delle categorie sono lette dallo stesso `SEGNALAZIONE_REASON_LABELS`
 * usato dal template, garantendo parità preview ↔ email.
 */
import {
  SEGNALAZIONE_REASON_LABELS,
  type SegnalazioneReason,
} from '@app/shared';

interface EmailPreviewProps {
  clientName: string;
  praticaId: string;
  selectedReasons: SegnalazioneReason[];
  detail?: string | null;
}

export function EmailPreview({
  clientName,
  praticaId,
  selectedReasons,
  detail,
}: EmailPreviewProps) {
  return (
    <article
      aria-label="Anteprima email cliente"
      className="rounded-md border bg-muted/30 p-4 text-sm"
    >
      <header className="mb-2 border-b pb-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Oggetto
        </p>
        <p className="font-medium">
          Pratica #{praticaId.slice(0, 8)} bloccata — è richiesta la tua revisione
        </p>
      </header>
      <div className="space-y-3">
        <p>Ciao {clientName || '<nome cliente>'},</p>
        <p>
          La tua pratica <span className="font-mono">{praticaId}</span> è stata
          messa in stato <strong>bloccata</strong> dal nostro team. Per poter
          procedere ti chiediamo di rivedere i seguenti punti:
        </p>
        {selectedReasons.length === 0 ? (
          <p className="italic text-muted-foreground">
            Seleziona almeno una categoria per vedere l'anteprima dell'elenco.
          </p>
        ) : (
          <ul className="list-disc space-y-1 pl-5">
            {selectedReasons.map((reason) => (
              <li key={reason}>
                {SEGNALAZIONE_REASON_LABELS[reason] ?? reason}
              </li>
            ))}
          </ul>
        )}
        {selectedReasons.includes('ALTRO') && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Dettaglio
            </p>
            <p className="whitespace-pre-wrap">
              {detail?.trim()
                ? detail
                : '<dettaglio richiesto per la categoria "Altro">'}
            </p>
          </div>
        )}
        <p>
          Una volta corretto quanto sopra potrai inviare nuovamente la pratica
          dal portale.
        </p>
        <p className="text-muted-foreground">— Team Registrazione Contratti</p>
      </div>
    </article>
  );
}
